import { DialogDescription } from "@radix-ui/react-dialog";
import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useInventoryContext } from "../../../hooks/use-inventory";
import { CollectionsEnum } from "../../../lib/enums";
import { Unit, Bin } from "../../../lib/types";
import {
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogContent,
  DialogTitle,
  DialogClose,
  DialogTrigger,
  Button,
  Input
} from "../../../components/ui";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { UnitDeleteDialog } from "./unit-delete-dialog";
import { collection, doc } from "firebase/firestore";
import { useFirestore, useSigninCheck } from "../../../hooks/use-firebase";


type UnitDialogProps = {
  isShowing: boolean,
  editItemId?: string,
  close: () => void
}

export const UnitDialog: FC<UnitDialogProps> = ({
  isShowing,
  editItemId,
  close
}) => {
  const { getValues, register, formState, reset, watch } = useForm()
  const { updateInventoryElem, addInventoryElem, deleteUnitWithCascade, units, bins, addInventoryElem: addBin, updateInventoryElem: updateBin, deleteInventoryElem: deleteBin } = useInventoryContext()
  const db = useFirestore()
  const { data: signInData } = useSigninCheck()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [binLocations, setBinLocations] = useState<string[]>([])
  const [existingBins, setExistingBins] = useState<Bin[]>([])

  const numBays = watch("num_of_bays")

  useEffect(() => {
    if (editItemId) {
      const editItem = units.find((u) => u.id === editItemId)

      if (editItem) {
        reset({
          name: editItem.name,
          abbreviation: editItem.abbreviation,
          num_of_bays: editItem.num_of_bays,
        })

        // Load existing bins for this unit
        const unitBins = bins.filter((b) => b.unit === editItemId)
        setExistingBins(unitBins)
        setBinLocations(unitBins.map(b => b.location))
      } else {
        toast.error("error trying to edit")
      }

    } else {
      reset({
        name: "",
        abbreviation: "",
        num_of_bays: null,
      })
      setBinLocations([])
      setExistingBins([])
    }


  }, [editItemId, bins])

  // Update bin locations array when num_of_bays changes
  useEffect(() => {
    if (numBays && numBays > 0) {
      const currentLength = binLocations.length
      const targetLength = parseInt(numBays)

      if (targetLength > currentLength) {
        // Add empty strings for new bins
        setBinLocations([...binLocations, ...Array(targetLength - currentLength).fill("")])
      } else if (targetLength < currentLength) {
        // Trim the array if user reduced the number
        setBinLocations(binLocations.slice(0, targetLength))
      }
    } else if (numBays === 0 || numBays === "" || numBays === null) {
      setBinLocations([])
    }
  }, [numBays])

  const handleDelete = () => {
    if (editItemId) {
      deleteUnitWithCascade(editItemId)
      setShowDeleteConfirm(false)
      close()
    }
  }

  return (
    <>
      <Dialog
        open={isShowing}
        onOpenChange={close}
      >
        <DialogTrigger />
        <DialogContent className="dark:bg-neutral800 dark:border-neutral700">
          <DialogHeader>
            <DialogTitle className="dark:text-neutral100">{editItemId ? "Edit" : "Add"} Unit</DialogTitle>
          </DialogHeader>
        <DialogDescription className="flex flex-col gap-2 dark:text-neutral300">
          <div className="text-sm font-semibold text-neutral700 dark:text-neutral300">
            What are we going to call this unit:
          </div>
          <div className="w-full flex flex-col gap-2">
            <Input
              type="text"
              className="w-full"
              placeholder="unit name"
              {...register("name", {
                required: true,
                maxLength: 25,
              })}
            />
            <Input
              type="text"
              className="w-full"
              placeholder="abbreviation"
              {...register("abbreviation", {
                required: true,
                maxLength: 8,
              })}
            />
          </div>
          <div className="text-sm font-semibold text-neutral700 dark:text-neutral300">How many bays?</div>
          <div className="w-full">
            <Input
              type="number"
              className="w-1/4"
              placeholder="6"
              {...register("num_of_bays", {
                required: true
              })}
            />
          </div>

          {/* Bin location inputs */}
          {numBays && parseInt(numBays) > 0 && (
            <div className="mt-4">
              <div className="text-sm font-semibold text-neutral700 dark:text-neutral300 mb-2">
                Bin Locations ({binLocations.length} of {numBays})
              </div>
              <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
                {binLocations.map((location, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="text-xs text-neutral500 dark:text-neutral400 w-8">
                      {index + 1}.
                    </span>
                    <Input
                      type="text"
                      className="flex-1"
                      placeholder={`Bin ${index + 1} location (e.g., Aisle A, Bay ${index + 1})`}
                      value={location}
                      onChange={(e) => {
                        const newLocations = [...binLocations]
                        newLocations[index] = e.target.value
                        setBinLocations(newLocations)
                      }}
                    />
                  </div>
                ))}
              </div>
              <p className="text-xs text-neutral500 dark:text-neutral400 mt-2">
                Bins with empty locations will be skipped
              </p>
            </div>
          )}
        </DialogDescription>
        <DialogFooter className="flex gap-2">
          {editItemId && (
            <Button
              variant="destructive"
              onClick={() => setShowDeleteConfirm(true)}
              className="mr-auto"
            >
              <Trash2 width={16} height={16} />
              Delete
            </Button>
          )}
          <DialogClose>
            <Button
              variant="outline"
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            onClick={async () => {
              const unit = {
                name: getValues("name"),
                abbreviation: getValues("abbreviation"),
                num_of_bays: getValues("num_of_bays")
              } as Unit

              try {
                let unitId = editItemId

                // Save unit first
                if (editItemId) {
                  unit.id = editItemId
                  await updateInventoryElem(unit, CollectionsEnum.UNIT)
                } else {
                  // Generate a new document reference with auto-generated ID
                  const newUnitRef = doc(collection(db, `users/${signInData?.user?.uid}/${CollectionsEnum.UNIT}`))
                  unitId = newUnitRef.id
                  unit.id = unitId
                  await addInventoryElem(unit, CollectionsEnum.UNIT)
                }

                // Now handle bins
                const filledBinLocations = binLocations.filter(loc => loc.trim() !== "")

                if (filledBinLocations.length > 0) {
                  // For editing, we need to compare with existing bins
                  if (editItemId) {
                    // Update existing bins or create new ones
                    for (let i = 0; i < filledBinLocations.length; i++) {
                      const location = filledBinLocations[i]
                      const existingBin = existingBins[i]

                      if (existingBin) {
                        // Update existing bin
                        await updateBin({
                          id: existingBin.id,
                          location: location,
                          unit: unitId
                        } as Bin, CollectionsEnum.BIN)
                      } else {
                        // Create new bin
                        await addBin({
                          location: location,
                          unit: unitId
                        } as Bin, CollectionsEnum.BIN)
                      }
                    }

                    // Delete any extra bins that were removed
                    if (existingBins.length > filledBinLocations.length) {
                      for (let i = filledBinLocations.length; i < existingBins.length; i++) {
                        await deleteBin(existingBins[i].id, CollectionsEnum.BIN)
                      }
                    }
                  } else {
                    // Create all new bins
                    for (const location of filledBinLocations) {
                      await addBin({
                        location: location,
                        unit: unitId
                      } as Bin, CollectionsEnum.BIN)
                    }
                  }

                  toast.success(`${unit.name} ${editItemId ? 'updated' : 'added'} with ${filledBinLocations.length} bin(s)`)
                } else {
                  toast.success(`${unit.name} ${editItemId ? 'updated' : 'added to units'}`)
                }
              } catch (e) {
                console.error(e)
                toast.error("Error saving unit and bins")
              }
              close()
            }}
            disabled={!formState.isValid}
          >
            {editItemId ? "Save Changes" : "Add Unit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <UnitDeleteDialog
      isShowing={showDeleteConfirm}
      unitName={getValues("name") || ""}
      unitAbbreviation={getValues("abbreviation") || ""}
      onClose={() => setShowDeleteConfirm(false)}
      onConfirmDelete={handleDelete}
    />
    </>
  )
}
